import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import {
  GetVersionsQuery,
  UnitSystemEnum,
  ShiftType,
  LogsTemplate,
  SectionUnitsEnum,
  TemplateFieldTypeEnum,
} from '@/__generated__/graphql';
import { selectedWorklogsTemplate } from '../state';
import { HistoryEntry, SectionChange, FieldChange } from '../components/Panel/historyTypes';
import { getSectionUnitSymbol, convertValueByUnit, getSectionUnitForField } from '../utils';
import * as globalState from '@/components/Analysis/state';
import { roundNumber } from '@/utils/unitSystem';

type Version = GetVersionsQuery['allVersions'][number];
type Template = LogsTemplate | undefined;

type TemplateFieldForHistory = NonNullable<NonNullable<Template>['fields']>[number];

const viewMetricValueForHistory = (
  raw: number,
  sectionUnit: SectionUnitsEnum | null | undefined,
  unitSystem: UnitSystemEnum,
  field: TemplateFieldForHistory | undefined
): number => {
  const decimals = field?.roundToDecimal ?? 3;
  if (sectionUnit === SectionUnitsEnum.Area) {
    return convertValueByUnit(raw, sectionUnit, unitSystem, 'view', {
      viewRoundDecimals: decimals,
    });
  }
  const converted = convertValueByUnit(raw, sectionUnit, unitSystem, 'view');
  return roundNumber(converted, decimals);
};

type FieldValue = {
  fieldKey: string;
  value: number | string;
  createdAt?: {
    $date: string;
  };
  updatedAt?: {
    $date: string;
  };
};

type LogEntryVersionData = {
  fieldValues: FieldValue[];
  type: string | null;
  shift: ShiftType | null;
};

type FileEntry = {
  name: string;
  fileName: string;
  /** Copied from parent log entry when the file is saved (Mongo subdocument). */
  logDate?: string;
  shift?: ShiftType;
};

const fileEntryHasLogDate = (f: FileEntry): f is FileEntry & { logDate: string } =>
  Boolean(f.logDate);

/** Prefer `fileName`, fall back to `name` (log-entry files may populate either). */
const logEntryFileIdentityKey = (file: FileEntry): string =>
  (file.fileName || file.name || '').trim();

const aggregateFieldValuesByField = (fieldValues: FieldValue[]): Record<string, number> => {
  const aggregated: Record<string, number> = {};
  for (const fieldValue of fieldValues) {
    if (typeof fieldValue.value === 'number') {
      aggregated[fieldValue.fieldKey] = (aggregated[fieldValue.fieldKey] || 0) + fieldValue.value;
    }
  }
  return aggregated;
};

const collectTextValuesByField = (fieldValues: FieldValue[]): Record<string, string> => {
  const textValues: Record<string, string> = {};
  for (const fieldValue of fieldValues) {
    if (typeof fieldValue.value === 'string' && fieldValue.value.length > 0) {
      textValues[fieldValue.fieldKey] = fieldValue.value;
    }
  }
  return textValues;
};

const getFileEntriesFromDotNotation = (data: Record<string, unknown> | null): FileEntry[] => {
  if (!data) return [];
  return Object.keys(data)
    .filter((key) => /^files\.\d+$/.test(key))
    .map((key) => data[key] as FileEntry);
};

const getAllFileEntries = (data: Record<string, unknown> | null): FileEntry[] => {
  if (!data) return [];

  if (Array.isArray(data.files)) {
    return data.files as FileEntry[];
  }

  const dotNotationFiles = getFileEntriesFromDotNotation(data);
  if (dotNotationFiles.length > 0) {
    return dotNotationFiles;
  }

  return [];
};

const transformFileVersionToHistoryEntry = (version: Version): HistoryEntry | null => {
  const isRemoval = version.lastModifiedFor === 'Attachment removed successfully';
  const updatedData = version.updatedData as Record<string, unknown> | null;
  const previousData = version.previousData as Record<string, unknown> | null;

  let documentCount = 1;
  let logDate: string | undefined;
  let shift: ShiftType | null = null;

  if (isRemoval) {
    const prevFiles = getAllFileEntries(previousData);
    const currFiles = getAllFileEntries(updatedData);
    documentCount = prevFiles.length - currFiles.length;
    if (documentCount <= 0) return null;

    if (prevFiles.length > 0) {
      const filesWithDates = prevFiles
        .filter(fileEntryHasLogDate)
        .sort((a, b) => new Date(b.logDate).getTime() - new Date(a.logDate).getTime());
      logDate = filesWithDates[0]?.logDate;
      // Backward compatibility: default to Day when legacy workpack files have no shift.
      shift = filesWithDates[0]?.shift || ShiftType.Day;
    }
  } else {
    const prevFiles = getAllFileEntries(previousData);
    const currFiles = getAllFileEntries(updatedData);

    const prevFileNames = new Set(prevFiles.map((f) => f.fileName).filter(Boolean));
    const newFiles = currFiles.filter((f) => !prevFileNames.has(f.fileName));

    if (newFiles.length === 0) return null;

    documentCount = newFiles.length;
    const filesWithDates = newFiles
      .filter(fileEntryHasLogDate)
      .sort((a, b) => new Date(b.logDate).getTime() - new Date(a.logDate).getTime());
    logDate = filesWithDates[0]?.logDate;
    // Backward compatibility: default to Day when legacy workpack files have no shift.
    shift =
      filesWithDates[0]?.shift || (updatedData?.shift as ShiftType | undefined) || ShiftType.Day;
  }

  const timestamp = version.createdAt;

  return {
    id: `${version.id}-file`,
    timestamp,
    userName: version.lastModifiedByUser?.name || 'Unknown User',
    type: 'document_attached',
    documentAction: isRemoval ? 'removed' : 'attached',
    documentCount,
    documentDate: logDate
      ? new Date(logDate).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC',
        })
      : undefined,
    shift,
  };
};

type AggregatedFieldSnapshot = {
  numeric: Record<string, number>;
  text: Record<string, string>;
};

const buildHistoryEntryFromAggregated = (
  version: Version,
  template: Template,
  unitSystem: UnitSystemEnum,
  updatedSnapshot: AggregatedFieldSnapshot,
  previousSnapshot: AggregatedFieldSnapshot,
  shift?: ShiftType | null
): HistoryEntry | null => {
  const updatedAggregated = updatedSnapshot.numeric;
  const previousAggregated = previousSnapshot.numeric;
  const updatedTextValues = updatedSnapshot.text;
  const previousTextValues = previousSnapshot.text;
  const sections: SectionChange[] = [];
  const standaloneFields: FieldChange[] = [];

  const sectionFieldKeys = new Set<string>();
  if (template?.sections) {
    for (const section of template.sections) {
      for (const fieldKey of section.fieldKeys) {
        sectionFieldKeys.add(fieldKey);
      }
    }
  }

  const textareaFieldKeys = new Set<string>();
  if (template?.fields) {
    for (const field of template.fields) {
      if (field.key && field.type === TemplateFieldTypeEnum.Textarea) {
        textareaFieldKeys.add(field.key);
      }
    }
  }

  const pushTextFieldChange = (fieldKey: string, field: TemplateFieldForHistory | undefined) => {
    const beforeText = previousTextValues[fieldKey] ?? '';
    const updatedText = updatedTextValues[fieldKey] ?? '';
    if (beforeText === updatedText) return;

    const isDeleted = beforeText.length > 0 && updatedText.length === 0;
    const textAction: 'added' | 'updated' = beforeText.length === 0 ? 'added' : 'updated';

    standaloneFields.push({
      category: field?.name || fieldKey,
      before: 0,
      change: 0,
      updated: 0,
      unit: '',
      deleted: isDeleted,
      isText: true,
      textAction,
      textBefore: beforeText,
      textUpdated: updatedText,
    });
  };

  // Process fields in sections
  if (template?.sections && template?.fields) {
    for (const section of template.sections) {
      const fields: FieldChange[] = [];
      let totalBefore = 0;
      let totalUpdated = 0;

      const isAreaSection = section.sectionUnit === SectionUnitsEnum.Area;
      let sumBeforeRawArea = 0;
      let sumUpdatedRawArea = 0;
      const sectionAreaViewDecimals =
        isAreaSection && section.fieldKeys.length > 0
          ? Math.max(
              ...section.fieldKeys.map((fieldKey) => {
                const sectionField = template.fields.find((f) => f.key === fieldKey);
                return sectionField?.roundToDecimal ?? 3;
              })
            )
          : 3;

      for (const fieldKey of section.fieldKeys) {
        const field = template.fields.find((f) => f.key === fieldKey);
        if (!field) continue;
        if (textareaFieldKeys.has(fieldKey)) continue;

        const beforeRaw = previousAggregated[fieldKey] ?? 0;
        const updatedRaw = updatedAggregated[fieldKey] ?? 0;
        const isDeleted = !(fieldKey in updatedAggregated) && beforeRaw > 0;

        const before = viewMetricValueForHistory(beforeRaw, section.sectionUnit, unitSystem, field);
        const updated = viewMetricValueForHistory(
          updatedRaw,
          section.sectionUnit,
          unitSystem,
          field
        );
        const change = updated - before;

        if (change !== 0 || isDeleted) {
          const unitSymbol = getSectionUnitSymbol(section.sectionUnit, unitSystem) || '';
          fields.push({
            category: field.name,
            before,
            change,
            updated,
            unit: unitSymbol,
            deleted: isDeleted,
          });
          if (isAreaSection) {
            sumBeforeRawArea += beforeRaw;
            sumUpdatedRawArea += updatedRaw;
          } else {
            totalBefore += before;
            totalUpdated += updated;
          }
        }
      }

      if (fields.length > 0) {
        const unitSymbol = getSectionUnitSymbol(section.sectionUnit, unitSystem) || '';
        const sectionDeleted = fields.every((f) => f.deleted);
        if (isAreaSection) {
          totalBefore = convertValueByUnit(
            sumBeforeRawArea,
            section.sectionUnit,
            unitSystem,
            'view',
            {
              viewRoundDecimals: sectionAreaViewDecimals,
            }
          );
          totalUpdated = convertValueByUnit(
            sumUpdatedRawArea,
            section.sectionUnit,
            unitSystem,
            'view',
            {
              viewRoundDecimals: sectionAreaViewDecimals,
            }
          );
        }
        sections.push({
          sectionTitle: section.title,
          totalBefore,
          totalChange: totalUpdated - totalBefore,
          totalUpdated,
          unit: unitSymbol,
          fields,
          deleted: sectionDeleted,
        });
      }
    }
  }

  if (template?.fields) {
    for (const field of template.fields) {
      if (!field.key) continue;
      if (sectionFieldKeys.has(field.key)) continue;
      if (textareaFieldKeys.has(field.key)) continue;

      const beforeRaw = previousAggregated[field.key] ?? 0;
      const updatedRaw = updatedAggregated[field.key] ?? 0;
      const isDeleted = !(field.key in updatedAggregated) && beforeRaw > 0;

      // Get section unit for this field to apply proper conversion
      const sectionUnit = getSectionUnitForField(field.key, template.sections);

      const before = viewMetricValueForHistory(beforeRaw, sectionUnit, unitSystem, field);
      const updated = viewMetricValueForHistory(updatedRaw, sectionUnit, unitSystem, field);
      const change = updated - before;

      if (change !== 0 || isDeleted) {
        const unitSymbol = sectionUnit ? getSectionUnitSymbol(sectionUnit, unitSystem) || '' : '';
        standaloneFields.push({
          category: field.name,
          before,
          change,
          updated,
          unit: unitSymbol,
          deleted: isDeleted,
        });
      }
    }
  }

  const allFieldKeys = new Set([
    ...Object.keys(updatedAggregated),
    ...Object.keys(previousAggregated),
  ]);

  for (const fieldKey of allFieldKeys) {
    if (sectionFieldKeys.has(fieldKey)) continue;
    if (template?.fields?.some((f) => f.key === fieldKey)) continue;

    const beforeRaw = previousAggregated[fieldKey] ?? 0;
    const updatedRaw = updatedAggregated[fieldKey] ?? 0;
    const isDeleted = !(fieldKey in updatedAggregated) && beforeRaw > 0;

    // Get section unit for this field to apply proper conversion
    const sectionUnit = getSectionUnitForField(fieldKey, template?.sections);

    const fieldForKey = template?.fields?.find((f) => f.key === fieldKey);
    const before = viewMetricValueForHistory(beforeRaw, sectionUnit, unitSystem, fieldForKey);
    const updated = viewMetricValueForHistory(updatedRaw, sectionUnit, unitSystem, fieldForKey);
    const change = updated - before;

    if (change !== 0 || isDeleted) {
      const unitSymbol = sectionUnit ? getSectionUnitSymbol(sectionUnit, unitSystem) || '' : '';
      standaloneFields.push({
        category: fieldForKey?.name || fieldKey,
        before,
        change,
        updated,
        unit: unitSymbol,
        deleted: isDeleted,
      });
    }
  }

  for (const fieldKey of textareaFieldKeys) {
    const field = template?.fields?.find((f) => f.key === fieldKey);
    pushTextFieldChange(fieldKey, field);
  }

  if (sections.length === 0 && standaloneFields.length === 0) return null;

  const timestamp = version.createdAt;

  return {
    id: `${version.id}-worklog`,
    timestamp,
    userName:
      version.lastModifiedByUser?.name || version.lastModifiedByUser?.email || 'Unknown User',
    type: 'hours_update',
    sections: sections.length > 0 ? sections : undefined,
    standaloneFields: standaloneFields.length > 0 ? standaloneFields : undefined,
    shift,
  };
};

const transformLogEntryVersionToHistoryEntry = (
  version: Version,
  template: Template,
  unitSystem: UnitSystemEnum
): HistoryEntry | null => {
  const updatedData = version.updatedData as LogEntryVersionData | null;
  const previousData = version.previousData as LogEntryVersionData | null;

  if (!updatedData?.fieldValues || !Array.isArray(updatedData.fieldValues)) return null;

  const updatedSnapshot: AggregatedFieldSnapshot = {
    numeric: aggregateFieldValuesByField(updatedData.fieldValues),
    text: collectTextValuesByField(updatedData.fieldValues),
  };
  const previousSnapshot: AggregatedFieldSnapshot = {
    numeric: previousData?.fieldValues
      ? aggregateFieldValuesByField(previousData.fieldValues)
      : {},
    text: previousData?.fieldValues ? collectTextValuesByField(previousData.fieldValues) : {},
  };

  const shift = updatedData.shift;

  return buildHistoryEntryFromAggregated(
    version,
    template,
    unitSystem,
    updatedSnapshot,
    previousSnapshot,
    shift
  );
};

/** LogEntry file list from version payload (array or dot-notation from Mongo updates). */
const getLogEntryFileEntries = (data: Record<string, unknown> | null): FileEntry[] => {
  if (!data) return [];
  if (Array.isArray(data.files)) {
    return (data.files as FileEntry[]).map((f) => ({ ...f }));
  }
  const dotNotation = getFileEntriesFromDotNotation(data);
  return dotNotation;
};

const formatLogEntryHistoryDate = (raw: string | Date | undefined | null): string | undefined => {
  if (raw === undefined || raw === null) return undefined;
  const date = raw instanceof Date ? raw : new Date(raw);
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
};

const pickNewestFileLogContext = (
  files: FileEntry[]
): { logDateRaw?: string | Date; shift?: ShiftType } => {
  const dated = files.filter(fileEntryHasLogDate);
  if (dated.length === 0) return {};
  const sorted = [...dated].sort(
    (a, b) => new Date(b.logDate).getTime() - new Date(a.logDate).getTime()
  );
  const first = sorted[0];
  return { logDateRaw: first.logDate, shift: first.shift };
};

const transformLogEntryFileVersionToHistoryEntry = (
  version: Version,
  qaqcLogEntryIdSet: ReadonlySet<string>
): HistoryEntry | null => {
  const isRemoval = version.lastModifiedFor === 'Attachment removed successfully';
  const updatedData = version.updatedData as Record<string, unknown> | null;
  const previousData = version.previousData as Record<string, unknown> | null;

  const prevFiles = getLogEntryFileEntries(previousData);
  const currFiles = getLogEntryFileEntries(updatedData);

  let documentCount: number;
  let filesForContext: FileEntry[];

  if (isRemoval) {
    documentCount = prevFiles.length - currFiles.length;
    if (documentCount <= 0) return null;
    const currNames = new Set(
      currFiles.map(logEntryFileIdentityKey).filter((key) => key.length > 0)
    );
    filesForContext = prevFiles.filter((f) => {
      const key = logEntryFileIdentityKey(f);
      return key.length > 0 && !currNames.has(key);
    });
    if (filesForContext.length === 0) {
      return null;
    }
  } else {
    const prevFileNames = new Set(
      prevFiles.map(logEntryFileIdentityKey).filter((key) => key.length > 0)
    );
    const newFiles = currFiles.filter((f) => {
      const key = logEntryFileIdentityKey(f);
      return key.length > 0 && !prevFileNames.has(key);
    });
    if (newFiles.length === 0) return null;
    documentCount = newFiles.length;
    filesForContext = newFiles;
  }

  // 1) Document-level (change stream enrichment). 2) Embedded file subdocs (persisted on upload).
  const docLogDateRaw = updatedData?.logDate ?? previousData?.logDate;
  const docShift =
    (updatedData?.shift as ShiftType | undefined) ?? (previousData?.shift as ShiftType | undefined);

  const fromFiles = pickNewestFileLogContext(filesForContext);
  const resolvedLogDateRaw = docLogDateRaw ?? fromFiles.logDateRaw;
  const documentDate = formatLogEntryHistoryDate(
    resolvedLogDateRaw as string | Date | undefined | null
  );
  const shift: ShiftType | null = docShift ?? fromFiles.shift ?? ShiftType.Day;

  const logEntryId = version.referenceId ?? '';
  const documentAttachmentTarget =
    logEntryId.length > 0 && qaqcLogEntryIdSet.has(logEntryId) ? 'job_phase_form' : 'worklog';

  return {
    id: `${version.id}-log-entry-file`,
    timestamp: version.createdAt,
    userName: version.lastModifiedByUser?.name || 'Unknown User',
    type: 'document_attached',
    documentAction: isRemoval ? 'removed' : 'attached',
    documentCount,
    documentDate,
    shift,
    documentAttachmentTarget,
  };
};

const transformWorkpackVersionToHistoryEntry = (version: Version): HistoryEntry | null => {
  // Only process file/attachment entries (backward compatibility for workpack attachments)
  return transformFileVersionToHistoryEntry(version);
};

/** Job phase / QA-QC panel stores nested `sections` (not top-level numeric `fieldValues`). */
const hasJobPhaseFormSectionsStructure = (data: unknown): boolean => {
  if (!data || typeof data !== 'object') return false;
  const sections = (data as { sections?: unknown }).sections;
  if (!Array.isArray(sections) || sections.length === 0) return false;
  return sections.some((section) => {
    if (!section || typeof section !== 'object') return false;
    const sectionRecord = section as { sectionKey?: unknown; entries?: unknown };
    return typeof sectionRecord.sectionKey === 'string' && Array.isArray(sectionRecord.entries);
  });
};

const isQaqcFormActivityVersion = (version: Version): boolean => {
  if (
    hasJobPhaseFormSectionsStructure(version.updatedData) ||
    hasJobPhaseFormSectionsStructure(version.previousData)
  ) {
    return true;
  }

  const key = (version.lastModifiedFor ?? '').trim().toLowerCase();
  if (!key) {
    return false;
  }
  if (key.includes('qa/qc') || key.includes('qaqc')) {
    return true;
  }
  if (key.includes('job phase')) {
    return true;
  }
  return false;
};

/**
 * `lastModifiedFor` is stored for auditing (often title case or imperative). The timeline uses
 * `{userName} {description}.` — map known values to a short past-tense phrase so it reads as English.
 */
const JOB_PHASE_HISTORY_LINE_BY_LAST_MODIFIED: Record<string, string> = {
  'job phase form submitted': 'submitted the job phase form',
  'job phase form edited': 'edited the job phase form',
  'submit job phase form': 'submitted the job phase form',
  'edit job phase form': 'edited the job phase form',
  'submitted job phase form': 'submitted the job phase form',
  'edited job phase form': 'edited the job phase form',
};

const jobPhaseFormDescriptionFromLastModifiedFor = (
  lastModifiedFor: string | null | undefined
): string => {
  const text = (lastModifiedFor ?? '').trim();
  if (!text) {
    return 'updated the job phase form';
  }
  const key = text.toLowerCase();
  return JOB_PHASE_HISTORY_LINE_BY_LAST_MODIFIED[key] ?? key;
};

const transformQaqcFormLogEntryVersion = (
  version: Version,
  logEntryShiftById: Record<string, ShiftType | null | undefined>
): HistoryEntry | null => {
  if (!isQaqcFormActivityVersion(version)) {
    return null;
  }

  const activityKey = (version.lastModifiedFor ?? '').trim().toLowerCase();
  if (activityKey === 'initialization') {
    return null;
  }

  const referenceId = version.referenceId ?? '';
  const shiftFromLogEntry = referenceId.length > 0 ? logEntryShiftById[referenceId] : undefined;
  const updatedData = version.updatedData as { shift?: ShiftType | null } | null;
  const shift = shiftFromLogEntry ?? updatedData?.shift ?? null;

  return {
    id: `${version.id}-qaqc-form`,
    timestamp: version.createdAt,
    userName:
      version.lastModifiedByUser?.name || version.lastModifiedByUser?.email || 'Unknown User',
    type: 'qaqc_form',
    description: jobPhaseFormDescriptionFromLastModifiedFor(version.lastModifiedFor),
    shift,
  };
};

export const useKpiHistoryTransformations = (
  logEntryShiftById: Record<string, ShiftType | null | undefined>,
  qaqcLogEntryIdSet: ReadonlySet<string>
) => {
  const template = useAtomValue(selectedWorklogsTemplate);
  const unitSystem = useAtomValue(globalState.unitSystem);

  const transformVersionsToHistoryEntries = useMemo(
    () => ({
      transformWorkpackVersion: (version: Version) =>
        transformWorkpackVersionToHistoryEntry(version),
      transformLogEntryVersion: (version: Version) =>
        transformLogEntryVersionToHistoryEntry(version, template, unitSystem),
      transformLogEntryFileVersion: (version: Version) =>
        transformLogEntryFileVersionToHistoryEntry(version, qaqcLogEntryIdSet),
      transformQaqcFormLogEntryVersion: (version: Version) =>
        transformQaqcFormLogEntryVersion(version, logEntryShiftById),
    }),
    [template, unitSystem, logEntryShiftById, qaqcLogEntryIdSet]
  );

  return transformVersionsToHistoryEntries;
};
